import { useQuery } from '@tanstack/react-query';
import {
	BulkAccountLoader,
	VelocityClient,
	PublicKey,
} from '@velocity-exchange/sdk';
import { VaultClient } from '@velocity-exchange/vaults-sdk';
import { Connection } from '@solana/web3.js';
import { UiVaults } from 'src/constants/vaults';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import {
	getMultipleOnChainVaultData,
	VaultsOnChainDataLookup,
} from 'src/hooks/vaults/vaultStatsSyncUtils';
import { VAULT_REFETCH_INTERVAL } from '../consts';
import { vaultQueryKeys } from '../vaultQueryKeys';

async function fetchNativeVaultOnChainData(
	driftClient: VelocityClient,
	vaultClient: VaultClient,
	connection: Connection,
	bulkAccountLoader: BulkAccountLoader
): Promise<VaultsOnChainDataLookup> {
	const nativeVaults = UiVaults.getNativeVaults();
	const vaultPubkeys = nativeVaults.map(
		(vault) => new PublicKey(vault.vaultPubkeyString)
	);

	return getMultipleOnChainVaultData(
		driftClient,
		vaultClient,
		connection,
		bulkAccountLoader,
		vaultPubkeys
	);
}

export function useVaultOnChainData() {
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();
	const vaultClient = useVaultsStore((s) => s.vaultClient);
	const connection = useDriftStore((s) => s.connection.current);
	const bulkAccountLoader = useDriftStore((s) => s.connection.accountLoader);
	const areVaultConfigsLoaded = useVaultsStore((s) => s.areVaultConfigsLoaded);

	const isReady =
		driftClientIsReady &&
		!!driftClient &&
		!!vaultClient &&
		!!connection &&
		!!bulkAccountLoader &&
		areVaultConfigsLoaded;

	return useQuery({
		queryKey: vaultQueryKeys.vaultOnChainData(),
		queryFn: () =>
			fetchNativeVaultOnChainData(
				driftClient!,
				vaultClient!,
				connection!,
				bulkAccountLoader!
			),
		enabled: isReady,
		staleTime: VAULT_REFETCH_INTERVAL,
		refetchInterval: VAULT_REFETCH_INTERVAL,
	});
}
